import type { PaletteKey, PixelCanvas } from '../../../lib';

/**
 * What the mythic guns' shots share (ADR-0160): the frames of a bullet, a muzzle flash and a burst, and a few ways to
 * paint them. Each set's shots are in a file of their own here (`<set>.ts`), gathered by `mythic-fx.sprite.ts`.
 */

export type Tone = PaletteKey | null;

/** Paints each pixel of `c` by its centre: `tone(x, y)`, null to leave it. */
export const paint = (c: PixelCanvas, tone: (x: number, y: number) => Tone): void => {
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++) {
      const t = tone(x + 0.5, y + 0.5);
      if (t !== null) c.set(x, y, t);
    }
};

/** The first tone of `steps` ([tone, under this]) `v` is under, else null. */
export const under = (v: number, steps: readonly (readonly [PaletteKey, number])[]): Tone => {
  for (const [tone, below] of steps) if (v < below) return tone;
  return null;
};

/** A jagged path from (x0, y0) to (x1, y1): its points between pushed off sideways by `offs` (x, y pairs). */
export function jagged(x0: number, y0: number, x1: number, y1: number, offs: readonly number[]): number[] {
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  const [nx, ny] = [-(y1 - y0) / len, (x1 - x0) / len];
  const pts = [x0, y0];
  offs.forEach((o, i) => {
    const t = (i + 1) / (offs.length + 1);
    pts.push(x0 + (x1 - x0) * t + nx * o, y0 + (y1 - y0) * t + ny * o);
  });
  pts.push(x1, y1);
  return pts;
}

/** A bullet at the stock pellet's size, its head right: turned along its flight. */
export const shot = { group: 'fx', size: [32, 16], origin: [21, 8], density: 4 } as const;
/** A muzzle flash pointing right from the muzzle (its origin, left-middle). */
export const muzzle = { group: 'fx', size: [48, 44], origin: [4, 20], density: 4 } as const;
/** The burst where a shot ends, round its middle. */
export const burst = { group: 'fx', size: [44, 44], origin: [22, 22], density: 4 } as const;

export const frames3 = (draw: (f: number) => (c: PixelCanvas) => void) => [draw(0), draw(1), draw(2)];
export const frames4 = (draw: (f: number) => (c: PixelCanvas) => void) => [draw(0), draw(1), draw(2), draw(3)];
